import type { FileItem } from "../types/file-item.types.ts"
import type { FilesSortOrder } from "../types/files-filters.types.ts"

/** Turns whatever the API sent for a date into something comparable. */
export type ParseSortDate = (value: string) => number

/** Ordering is unavoidably client-side here.
 *
 * The Files list is assembled from four independent paginated endpoints —
 * attachments, project documents, folders and albums — so there is no single
 * queryset for the server to order. What it must not do is compare the raw
 * values: `TimezoneFormattingMixin` renders every datetime in the viewer's own
 * `date_format`, so these arrive as `09/07/2026 09:15 AM` or
 * `Sep 07, 2026 09:15`. Comparing those as strings orders by month name or by
 * day-of-month, and is only correct by accident under ISO — the default, which
 * is exactly why it reads as working.
 */
export const sortFileItems = (
  items: FileItem[],
  order: FilesSortOrder,
  parseDate: ParseSortDate
): FileItem[] => {
  const sorted = [...items]
  const byDate = (key: "created" | "updated") => (a: FileItem, b: FileItem) =>
    parseDate(b[key]) - parseDate(a[key])

  switch (order) {
    case "added_desc":
      return sorted.sort(byDate("created"))
    case "name_asc":
      return sorted.sort((a, b) => a.name.localeCompare(b.name))
    case "name_desc":
      return sorted.sort((a, b) => b.name.localeCompare(a.name))
    case "size_desc":
      return sorted.sort((a, b) => (b.fileSize ?? 0) - (a.fileSize ?? 0))
    case "size_asc":
      return sorted.sort((a, b) => (a.fileSize ?? 0) - (b.fileSize ?? 0))
    case "modified_desc":
    default:
      return sorted.sort(byDate("updated"))
  }
}
